import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import { ToothDetailPanel } from './ToothDetailPanel';
import type { ToothData, ToothNotation } from './types';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothDetailDrawerProps {
  /** Currently selected teeth FDI strings */
  selectedTeeth: string[];
  /** Data for the single selected tooth (when selectedTeeth.length === 1) */
  tooth: ToothData | null;
  notation: ToothNotation;
  onSave?: (tooth: ToothData) => void;
  onClose: () => void;
  onClearSelection: () => void;
  readOnly?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ToothDetailDrawer({
  selectedTeeth,
  tooth,
  notation,
  onSave,
  onClose,
  onClearSelection,
  readOnly,
}: ToothDetailDrawerProps) {
  const { t } = useTranslation();
  const isOpen = selectedTeeth.length > 0;
  const isMulti = selectedTeeth.length > 1;

  // ESC key closes
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-black/30 transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel — 720px on desktop, 90vw on tablet, full width on phone */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tooth-detail-drawer-title"
        className={cn(
          'fixed right-0 top-0 z-40 h-full',
          'w-full sm:w-[min(720px,90vw)]',
          'bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700',
          'flex flex-col overflow-hidden',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 flex-shrink-0">
          <h2 id="tooth-detail-drawer-title" className="text-sm font-semibold text-slate-900 dark:text-white">
            {isMulti
              ? t('dental.chart.drawer.multiSelected', { count: selectedTeeth.length })
              : tooth
                ? `Tand ${selectedTeeth[0]}`
                : 'Tanddetaljer'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 transition-colors"
            aria-label={t('dental.chart.drawer.close', 'Luk')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isMulti ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                {t('dental.chart.drawer.multiSelected', { count: selectedTeeth.length })}
              </p>
              <p className="text-xs text-slate-400">
                Batch-redigering tilgås i TAND-4
              </p>
              <button
                type="button"
                onClick={onClearSelection}
                className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('dental.chart.drawer.clearSelection', 'Ryd valg')}
              </button>
            </div>
          ) : (
            <div className="p-4">
              <ToothDetailPanel
                tooth={tooth}
                notation={notation}
                onSave={onSave}
                onClose={onClose}
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
